import React, { useState, useEffect, useCallback } from 'react';
import { auth, db } from '../firebase/config';
import { signOut } from 'firebase/auth';
import {
  collection, getDocs, doc, setDoc, updateDoc,
  deleteDoc, query, orderBy, writeBatch
} from 'firebase/firestore';
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  getDay, isToday, isBefore, startOfDay,
  subMonths, addMonths, addWeeks, parseISO
} from 'date-fns';
import Layout from '../components/Layout';

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const VIEW_TOKEN = 'gh-view-a1d2i3';

// ── HELPERS ──
function isFutureDay(date) {
  return !isBefore(startOfDay(date), startOfDay(new Date()));
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h);
  return `${hour % 12 || 12}:${m} ${hour < 12 ? 'AM' : 'PM'}`;
}

function recurringLabel(entry) {
  if (!entry.recurring || entry.recurring === 'none') return '';
  const freq = entry.recurring === 'weekly' ? 'Weekly' : 'Monthly';
  const limit = entry.recurringLimit === 0
    ? ' (indefinite)'
    : entry.recurringLimit === 1 ? ' (1 occurrence)' : ` (${entry.recurringLimit} occurrences)`;
  return `${freq}${limit}`;
}

// Generate all occurrences of a recurring entry within a month
function expandRecurring(entry, monthStart, monthEnd) {
  if (!entry.recurring || entry.recurring === 'none') return [];

  const results = [];
  const baseDate = parseISO(entry.date);
  const limit = entry.recurringLimit || 0; // 0 = indefinite
  const maxOccurrences = limit === 0 ? 500 : limit;

  let current = baseDate;
  let count = 0;

  while (count < maxOccurrences) {
    // Skip the base date itself (it's already its own entry)
    if (count > 0) {
      const dayStart = startOfDay(current);
      if (!isBefore(dayStart, startOfDay(monthStart)) && !isBefore(startOfDay(monthEnd), dayStart)) {
        results.push({
          ...entry,
          id: `${entry.id}_recur_${count}`,
          date: format(current, 'yyyy-MM-dd'),
          isRecurInstance: true,
          recurParentId: entry.id,
          recurIndex: count,
        });
      }
      // Stop if past month end (for efficiency, but only if not indefinite)
      if (isBefore(startOfDay(monthEnd), dayStart) && limit !== 0) break;
      if (isBefore(startOfDay(monthEnd), dayStart) && count > 100) break;
    }
    count++;
    if (entry.recurring === 'weekly') {
      current = addWeeks(baseDate, count);
    } else {
      // Monthly: same date next month
      const next = new Date(baseDate);
      next.setMonth(baseDate.getMonth() + count);
      current = next;
    }
  }
  return results;
}

export default function AdminCalendar() {
  const [viewDate, setViewDate] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('calendar');
  const [showShare, setShowShare] = useState(false);
  const [copied, setCopied] = useState(false);

  // Modal state
  const [modal, setModal] = useState(null); // null | 'add' | 'edit' | 'recurScope' | 'detail'
  const [selectedDate, setSelectedDate] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [detailEntry, setDetailEntry] = useState(null);
  const [recurScopeAction, setRecurScopeAction] = useState(null); // 'save' | 'delete'
  const [pendingFormData, setPendingFormData] = useState(null);

  const [form, setForm] = useState({
    date: '', time: '', title: '', description: '',
    recurring: 'none',         // 'none' | 'weekly' | 'monthly'
    recurringLimit: '',        // '' = indefinite, number = N occurrences
    recurringIndefinite: true, // checkbox
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const shareUrl = `${window.location.origin}/view/${VIEW_TOKEN}`;

  // ── FETCH ──
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'entries'), orderBy('date')));
      setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // ── ALL VISIBLE ENTRIES for month (base + recurring instances) ──
  function getMonthEntries() {
    const monthStart = startOfMonth(viewDate);
    const monthEnd = endOfMonth(viewDate);
    const monthKey = format(viewDate, 'yyyy-MM');

    let all = [];
    entries.forEach(entry => {
      // Add if base date falls in this month
      if (entry.date.startsWith(monthKey)) {
        all.push(entry);
      }
      // Expand recurring instances that fall in this month
      if (entry.recurring && entry.recurring !== 'none') {
        const instances = expandRecurring(entry, monthStart, monthEnd);
        all = all.concat(instances);
      }
    });
    return all;
  }

  function getEntriesForDay(date) {
    const key = format(date, 'yyyy-MM-dd');
    return getMonthEntries().filter(e => e.date === key);
  }

  // ── OPEN ADD ──
  function openAdd(date) {
    setSelectedDate(date);
    setEditEntry(null);
    setForm({
      date: format(date, 'yyyy-MM-dd'),
      time: '', title: '', description: '',
      recurring: 'none', recurringLimit: '', recurringIndefinite: true,
    });
    setFormError('');
    setModal('add');
  }

  // ── OPEN EDIT ──
  function openEdit(entry, e) {
    if (e) e.stopPropagation();
    // If it's a recurring instance, load the parent entry for editing
    const baseEntry = entry.isRecurInstance
      ? entries.find(en => en.id === entry.recurParentId) || entry
      : entry;
    setEditEntry(baseEntry);
    setForm({
      date: baseEntry.date,
      time: baseEntry.time || '',
      title: baseEntry.title || '',
      description: baseEntry.description || '',
      recurring: baseEntry.recurring || 'none',
      recurringLimit: baseEntry.recurringLimit || '',
      recurringIndefinite: !baseEntry.recurringLimit || baseEntry.recurringLimit === 0,
    });
    setFormError('');
    setModal('edit');
  }

  // ── OPEN DETAIL (read-only popup for clicking an entry) ──
  function openDetail(entry, e) {
    if (e) e.stopPropagation();
    setDetailEntry(entry);
    setModal('detail');
  }

  // ── SAVE ──
  async function handleSave(e) {
    e.preventDefault();
    if (!form.title.trim()) { setFormError('Please enter a title.'); return; }

    const data = {
      date: form.date,
      time: form.time,
      title: form.title.trim(),
      description: form.description.trim(),
      recurring: form.recurring,
      recurringLimit: form.recurring !== 'none'
        ? (form.recurringIndefinite ? 0 : (parseInt(form.recurringLimit) || 0))
        : 0,
      updatedAt: new Date().toISOString(),
    };

    // If editing a recurring entry, ask scope
    if (modal === 'edit' && editEntry && editEntry.recurring && editEntry.recurring !== 'none') {
      setPendingFormData(data);
      setRecurScopeAction('save');
      setModal('recurScope');
      return;
    }

    await doSave(data, 'all');
  }

  async function doSave(data, scope) {
    setSaving(true);
    try {
      if (modal === 'edit' && editEntry) {
        if (scope === 'all') {
          // Update the base entry — all future instances follow automatically
          await updateDoc(doc(db, 'entries', editEntry.id), data);
        } else {
          // scope === 'one': create an exception entry for just this occurrence
          // Mark the parent to skip this date, save a one-off copy
          const exceptions = editEntry.exceptions || [];
          if (!exceptions.includes(editEntry.date)) {
            await updateDoc(doc(db, 'entries', editEntry.id), {
              exceptions: [...exceptions, editEntry.date]
            });
          }
          const oneOffId = `${editEntry.id}_exception_${editEntry.date}`;
          await setDoc(doc(db, 'entries', oneOffId), {
            ...data,
            recurring: 'none',
            recurringLimit: 0,
            isException: true,
            exceptionParentId: editEntry.id,
            createdAt: new Date().toISOString(),
          });
        }
      } else {
        // New entry
        const id = `${data.date}-${Date.now()}`;
        await setDoc(doc(db, 'entries', id), { ...data, createdAt: new Date().toISOString() });
      }
      setModal(null);
      fetchEntries();
    } catch (err) {
      setFormError('Error saving. Please try again.');
      console.error(err);
    }
    setSaving(false);
  }

  // ── DELETE ──
  async function handleDelete() {
    if (!editEntry) return;
    if (editEntry.recurring && editEntry.recurring !== 'none') {
      setPendingFormData(null);
      setRecurScopeAction('delete');
      setModal('recurScope');
      return;
    }
    if (!window.confirm(`Delete "${editEntry.title}"?`)) return;
    await deleteDoc(doc(db, 'entries', editEntry.id));
    setModal(null);
    fetchEntries();
  }

  async function doDelete(scope) {
    setSaving(true);
    try {
      if (scope === 'all') {
        await deleteDoc(doc(db, 'entries', editEntry.id));
      } else {
        // scope === 'one': add exception so this occurrence is skipped
        const exceptions = editEntry.exceptions || [];
        await updateDoc(doc(db, 'entries', editEntry.id), {
          exceptions: [...exceptions, editEntry.date]
        });
      }
      setModal(null);
      fetchEntries();
    } catch (err) { console.error(err); }
    setSaving(false);
  }

  // ── COPY SHARE URL ──
  function copyShareUrl() {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  // ── CALENDAR RENDER ──
  function renderCalendar() {
    const start = startOfMonth(viewDate);
    const end = endOfMonth(viewDate);
    const days = eachDayOfInterval({ start, end });
    const startPad = getDay(start);

    return (
      <div className="cal-grid">
        {DAYS_SHORT.map(d => <div key={d} className="cal-day-hdr">{d}</div>)}
        {Array.from({ length: startPad }, (_, i) => <div key={`p${i}`} className="cal-day empty" />)}
        {days.map(date => {
          const dayEntries = getEntriesForDay(date);
          const future = isFutureDay(date);
          const today = isToday(date);
          return (
            <div
              key={date.toISOString()}
              className={`cal-day clickable${today ? ' today' : ''}${!future && !today ? ' past' : ''}`}
              onClick={() => openAdd(date)}
            >
              <div className="cal-day-num">
                {today
                  ? <span className="today-badge">{format(date, 'd')}</span>
                  : format(date, 'd')
                }
              </div>
              {dayEntries.map(entry => (
                <div
                  key={entry.id}
                  className={`cal-entry${future ? ' future' : ''}${entry.recurring && entry.recurring !== 'none' ? ' recurring-badge' : ''}`}
                  onClick={e => { e.stopPropagation(); openEdit(entry, e); }}
                  title={`${entry.title}${entry.description ? ' — ' + entry.description : ''}`}
                >
                  {entry.time ? `${formatTime(entry.time)} ` : ''}{entry.title}
                </div>
              ))}
              <button className="cal-add-btn" onClick={e => { e.stopPropagation(); openAdd(date); }}>+</button>
            </div>
          );
        })}
      </div>
    );
  }

  // ── LIST RENDER ──
  function renderList() {
    const monthKey = format(viewDate, 'yyyy-MM');
    const monthEntries = getMonthEntries()
      .filter(e => e.date.startsWith(monthKey))
      .sort((a, b) => a.date.localeCompare(b.date) || (a.time || '').localeCompare(b.time || ''));

    if (monthEntries.length === 0) {
      return (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <div style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, textTransform: 'uppercase', color: 'var(--navy)', marginBottom: 8 }}>
            No entries for {format(viewDate, 'MMMM yyyy')}
          </div>
          <p style={{ fontSize: 13 }}>Click any calendar day to add a service call.</p>
        </div>
      );
    }

    return (
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {monthEntries.map(entry => {
          const entryDate = parseISO(entry.date);
          const future = isFutureDay(entryDate);
          return (
            <div
              key={entry.id}
              className="list-entry"
              style={{ cursor: 'pointer' }}
              onClick={e => openEdit(entry, e)}
            >
              <div className={`list-date-block${future ? ' future' : ''}`}>
                <div className="list-date-day">{format(entryDate, 'd')}</div>
                <div className="list-date-mon">{format(entryDate, 'MMM')}</div>
              </div>
              <div className="list-entry-body">
                <div className="list-entry-title">
                  {entry.title}
                  {entry.recurring && entry.recurring !== 'none' && (
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400, marginLeft: 8 }}>↺ {recurringLabel(entry)}</span>
                  )}
                </div>
                <div className="list-entry-meta">
                  {entry.time && <span>🕐 {formatTime(entry.time)} &nbsp;·&nbsp; </span>}
                  {format(entryDate, 'EEEE, MMMM d, yyyy')}
                  {future && <span style={{ marginLeft: 8, background: 'var(--red)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3, textTransform: 'uppercase' }}>Upcoming</span>}
                </div>
                {entry.description && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{entry.description}</div>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>Edit →</div>
            </div>
          );
        })}
      </div>
    );
  }

  // ── ENTRY FORM MODAL ──
  function renderFormModal() {
    const isEdit = modal === 'edit';
    return (
      <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
        <div className="modal">
          <div className="modal-header">
            <h3>{isEdit ? 'Edit Entry' : `Add Entry${selectedDate ? ' — ' + format(selectedDate, 'MMM d, yyyy') : ''}`}</h3>
            <button className="modal-close" onClick={() => setModal(null)}>✕</button>
          </div>
          <div className="modal-body">
            {formError && <div className="alert alert-danger">{formError}</div>}
            <form onSubmit={handleSave}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label>Date *</label>
                  <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>Time (optional)</label>
                  <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} />
                </div>
              </div>

              <div className="form-group">
                <label>Title / Service Type *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Washer preventative maintenance"
                  required autoFocus
                />
              </div>

              <div className="form-group">
                <label>Description (optional)</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Additional details…"
                  rows={2}
                />
              </div>

              {/* ── RECURRING SECTION ── */}
              <div className="form-group">
                <label>Recurring</label>
                <div className="recurring-box">
                  <div style={{ display: 'flex', gap: 16, marginBottom: form.recurring !== 'none' ? 14 : 0 }}>
                    {['none', 'weekly', 'monthly'].map(opt => (
                      <label key={opt} className="recurring-box" style={{ background: 'none', border: 'none', padding: 0, margin: 0 }}>
                        <input
                          type="radio"
                          name="recurring"
                          value={opt}
                          checked={form.recurring === opt}
                          onChange={() => setForm(f => ({ ...f, recurring: opt }))}
                          style={{ width: 'auto', marginRight: 6 }}
                        />
                        <span style={{ fontSize: 13, fontWeight: 500, textTransform: 'capitalize' }}>
                          {opt === 'none' ? 'Does not repeat' : opt === 'weekly' ? '↺ Weekly' : '↺ Monthly'}
                        </span>
                      </label>
                    ))}
                  </div>

                  {form.recurring !== 'none' && (
                    <div style={{ borderTop: '1px solid #c0d0e4', paddingTop: 12, marginTop: 4 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={form.recurringIndefinite}
                          onChange={e => setForm(f => ({ ...f, recurringIndefinite: e.target.checked, recurringLimit: '' }))}
                          style={{ width: 'auto' }}
                        />
                        Repeat indefinitely
                      </label>

                      {!form.recurringIndefinite && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Repeat for</span>
                          <input
                            type="number"
                            min="1"
                            max="260"
                            value={form.recurringLimit}
                            onChange={e => setForm(f => ({ ...f, recurringLimit: e.target.value }))}
                            style={{ width: 70, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 3, fontSize: 14, textAlign: 'center' }}
                            placeholder="—"
                          />
                          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                            {form.recurring === 'weekly' ? 'week(s)' : 'month(s)'}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer" style={{ padding: '14px 0 0', border: 'none', justifyContent: isEdit ? 'space-between' : 'flex-end' }}>
                {isEdit && (
                  <button type="button" className="btn btn-danger btn-sm" onClick={handleDelete}>
                    Delete
                  </button>
                )}
                <div className="flex gap-8">
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setModal(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Entry'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── DETAIL MODAL (view-only, same fields) ──
  function renderDetailModal() {
    const e = detailEntry;
    if (!e) return null;
    const entryDate = parseISO(e.date);
    const future = isFutureDay(entryDate);
    return (
      <div className="modal-overlay" onClick={ev => ev.target === ev.currentTarget && setModal(null)}>
        <div className="modal">
          <div className="modal-header" style={{ background: future ? 'var(--red)' : 'var(--navy)' }}>
            <h3>Service Entry</h3>
            <button className="modal-close" onClick={() => setModal(null)}>✕</button>
          </div>
          <div className="modal-body">
            <div className="detail-row">
              <span className="detail-label">Date</span>
              <span className="detail-value">{format(entryDate, 'EEEE, MMMM d, yyyy')}</span>
            </div>
            {e.time && (
              <div className="detail-row">
                <span className="detail-label">Time</span>
                <span className="detail-value">{formatTime(e.time)}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">Service</span>
              <span className="detail-value" style={{ fontWeight: 600 }}>{e.title}</span>
            </div>
            {e.description && (
              <div className="detail-row">
                <span className="detail-label">Details</span>
                <span className="detail-value">{e.description}</span>
              </div>
            )}
            {e.recurring && e.recurring !== 'none' && (
              <div className="detail-row">
                <span className="detail-label">Recurring</span>
                <span className="detail-value">↺ {recurringLabel(e)}</span>
              </div>
            )}
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {future ? '🔴 Upcoming service call' : '✓ Past service call'}
              </span>
              <div className="flex gap-8">
                <button className="btn btn-ghost btn-sm" onClick={() => setModal(null)}>Close</button>
                <button className="btn btn-navy btn-sm" onClick={ev => openEdit(e, ev)}>Edit This Entry</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── RECURRING SCOPE MODAL ──
  function renderRecurScopeModal() {
    const isDelete = recurScopeAction === 'delete';
    return (
      <div className="modal-overlay">
        <div className="modal" style={{ maxWidth: 420 }}>
          <div className="modal-header" style={{ background: isDelete ? '#8b0000' : 'var(--navy)' }}>
            <h3>{isDelete ? 'Delete Recurring Entry' : 'Edit Recurring Entry'}</h3>
            <button className="modal-close" onClick={() => setModal('edit')}>✕</button>
          </div>
          <div className="modal-body">
            <div className="alert alert-warning">
              This is a recurring entry. What would you like to {isDelete ? 'delete' : 'change'}?
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                className="btn btn-outline w-full"
                style={{ justifyContent: 'center', padding: '12px' }}
                onClick={() => {
                  if (isDelete) { doDelete('one'); }
                  else { doSave(pendingFormData, 'one'); }
                }}
                disabled={saving}
              >
                This occurrence only
              </button>
              <button
                className={`btn w-full ${isDelete ? 'btn-danger' : 'btn-primary'}`}
                style={{ justifyContent: 'center', padding: '12px' }}
                onClick={() => {
                  if (isDelete) { doDelete('all'); }
                  else { doSave(pendingFormData, 'all'); }
                }}
                disabled={saving}
              >
                {saving ? 'Working…' : `This and all future occurrences`}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout
      pageTitle="Preventative Maintenance Calendar"
      pageSubtitle={`Grand Hyatt San Antonio · ${format(viewDate, 'MMMM yyyy')}`}
      navExtra={<li style={{ marginLeft: 'auto' }}><button onClick={() => signOut(auth)}>Sign Out</button></li>}
    >
      {/* ── TOP BAR ── */}
      <div className="flex-between mb-20">
        <div className="flex gap-8">
          <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 3, overflow: 'hidden' }}>
            {['calendar', 'list'].map(mode => (
              <button
                key={mode}
                className="btn btn-sm"
                style={{ borderRadius: 0, background: viewMode === mode ? 'var(--navy)' : '#fff', color: viewMode === mode ? '#fff' : 'var(--text)', border: 'none', borderLeft: mode === 'list' ? '1px solid var(--border)' : 'none' }}
                onClick={() => setViewMode(mode)}
              >
                {mode === 'calendar' ? '📅 Calendar' : '☰ List'}
              </button>
            ))}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setViewDate(d => subMonths(d, 1))}>← Prev</button>
          <span style={{ fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 600, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.04em', alignSelf: 'center' }}>
            {format(viewDate, 'MMMM yyyy')}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => setViewDate(d => addMonths(d, 1))}>Next →</button>
          {format(viewDate, 'yyyy-MM') !== format(new Date(), 'yyyy-MM') && (
            <button className="btn btn-primary btn-sm" onClick={() => setViewDate(new Date())}>Today</button>
          )}
        </div>
        <div className="flex gap-8">
          <button className="btn btn-navy btn-sm" onClick={() => setShowShare(s => !s)}>🔗 Share Link</button>
          <button className="btn btn-primary btn-sm" onClick={() => openAdd(new Date())}>+ Add Entry</button>
        </div>
      </div>

      {/* ── SHARE PANEL ── */}
      {showShare && (
        <div className="share-box mb-20">
          <div style={{ fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--navy)', marginBottom: 6 }}>
            🔗 Grand Hyatt Read-Only View Link
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
            Share this private URL with Grand Hyatt staff. They can view the calendar without logging in.
          </p>
          <div className="share-url">{shareUrl}</div>
          <div className="flex gap-8 mt-16">
            <button className="btn btn-navy btn-sm" onClick={copyShareUrl}>{copied ? '✓ Copied!' : '📋 Copy Link'}</button>
            <a
              href={`mailto:?subject=Grand Hyatt Service Calendar&body=You have been given read-only access to the A-LDI preventative maintenance calendar for Grand Hyatt San Antonio.%0A%0ACalendar link: ${shareUrl}%0A%0AThis is a private link — please do not share it publicly.%0A%0AAmerican Laundry Distributors, Inc. | 210.661.5995`}
              className="btn btn-primary btn-sm"
            >✉ Email This Link</a>
          </div>
        </div>
      )}

      {/* ── MAIN VIEW ── */}
      {loading ? <div className="spinner" /> : viewMode === 'calendar' ? renderCalendar() : renderList()}

      {/* ── MODALS ── */}
      {(modal === 'add' || modal === 'edit') && renderFormModal()}
      {modal === 'detail' && renderDetailModal()}
      {modal === 'recurScope' && renderRecurScopeModal()}
    </Layout>
  );
}
